import Link from "next/link";
import { type ReactNode } from "react";

import {
  ICON_BUTTON_VARIANT_CLASSES,
  type IconButtonVariant,
} from "@/blueprint/components/IconButton/IconButtonVariant";
import { cn } from "@/lib/utils";
import { PRESS_SCALE_CLASSES } from "@/blueprint/tokens/Motion";

type IconButtonBaseProps = {
  variant?: IconButtonVariant;
  className?: string;
  children: ReactNode;
};

type IconButtonProps = IconButtonBaseProps &
  (
    | {
        /** Renders a link when set; a button otherwise, as [Button] does. */
        href: string;
        label: string;
        /** Opens in a new tab, with the rel a new tab needs. */
        external?: boolean;
        /** Native tooltip, for icon links that carry no visible label. */
        title?: string;
        onClick?: () => void;
        decorative?: never;
        pressed?: never;
        disabled?: never;
      }
    | {
        href?: undefined;
        label: string;
        external?: never;
        title?: never;
        onClick?: () => void;
        decorative?: never;
        pressed?: boolean;
        disabled?: boolean;
      }
    | {
        /**
         * An inert badge in the same shape and resting colours — what
         * [PlayButton] rendered when it was handed no handler. It takes no
         * accessible name, because whatever it marks is named by its
         * surroundings, and it lets the pointer through so the card or link
         * it sits inside keeps the hover.
         */
        decorative: true;
        label?: never;
        href?: never;
        external?: never;
        title?: never;
        onClick?: never;
        pressed?: never;
        disabled?: never;
      }
  );

const IconButton = ({
  variant = "primary",
  className,
  children,
  ...props
}: IconButtonProps) => {
  const classes = cn(
    "relative flex h-button w-button shrink-0 items-center justify-center rounded-full",
    /* Matches [Button]: colour over `quick`, the press faster and
       immediate, since Figma gives the press no colour of its own. */
    "transition-[color,background-color,border-color,box-shadow,transform] duration-quick ease-out-strong",
    PRESS_SCALE_CLASSES,
    "motion-reduce:transition-none",
    /* The disabled tint belongs to whichever variant has no Figma disabled
       state of its own, so it cannot stack on the ones that do. */
    ICON_BUTTON_VARIANT_CLASSES[variant],
    className,
  );

  if (props.decorative === true) {
    return (
      <span aria-hidden className={cn(classes, "pointer-events-none")}>
        {children}
      </span>
    );
  }

  if (props.href !== undefined) {
    return (
      <Link
        href={props.href}
        aria-label={props.label}
        className={classes}
        {...(props.title !== undefined ? { title: props.title } : {})}
        {...(props.external === true
          ? { target: "_blank", rel: "noopener noreferrer" }
          : {})}
        {...(props.onClick !== undefined ? { onClick: props.onClick } : {})}
      >
        {children}
      </Link>
    );
  }

  return (
    <button
      type="button"
      aria-label={props.label}
      aria-pressed={props.pressed}
      disabled={props.disabled}
      onClick={props.onClick}
      className={classes}
    >
      {children}
    </button>
  );
};

export default IconButton;
